import { create } from "zustand";

import { IAccountModalPayload } from "src/interfaces/modals/newAccountModal";
import { IEditCategoryModalPayload } from "src/pages/Categories/EditCategoryModal";
import { IEditClientProviderModalPayload } from "src/pages/Sources/EditClientProviderModal";
import { IRecurringTransactionModalPayload } from "src/components/Modals/FrequentTransactionModal";

const useModalsStore = create((set: any, get: any) => ({
  showDayColorsModal: false,
  showRecurringTransactionPaymentModal: false,
  recurringTransactionPaymentPayload: null,
  showMonthColorsModal: false,
  showPaginationSettingsModal: false,
  showCreateRecurringTransactionModal: false,
  showEditRecurringTransactionModal: false,
  editRecurringTransactionPayload: null,
  showEditClientProviderModal: false,
  editClientProviderModalPayload: null,
  showCreateNewAccountModal: false,
  showEditAccountModal: false,
  editAccountModalPayload: null,
  showCreateNewTask: false,
  showCreateNewLink: false,
  showEditProfile: false,
  showEditAccount: "",
  showDeleteHistoryConfirm: false,
  showLogoutDialog: false,
  showCreateNewCanvas: false,
  showMoneyTransferModal: false,
  showSearchModal: false,
  showTableSettingsModal: false,
  showContactQrCode: false,
  showCreatePassKey: false,
  showChangePassword: false,
  showNotificationsSettings: false,
  showPrivacySettings: false,
  showLanguageChange: false,
  showStartupSettings: false,
  showChartSettingsModal: false,
  showEmailNotificationsSettingsModal: false,
  showContactSearchModal: false,
  integrations: {
    showGmailConnectorModal: false,
    showOpenAiApiModal: false,
  },
  showFeedbackConfirmDialog: false,
  showEmailInviteModal: false,
  showAccountsSettingsModal: false,
  showTransactionSettingsModal: false,
  showThemeSettingsModal: false,
  showEditCategoryModal: false,
  editCategoryModalPayload: null,

  setShowLanguageChange: (isVisible: boolean) => {
    set({ showLanguageChange: isVisible });
  },

  setShowThemeSettingsModal: (isVisible: boolean) => {
    set({ showThemeSettingsModal: isVisible });
  },

  setShowAccountsSettingsModal: (isVisible: boolean) => {
    set({ showAccountsSettingsModal: isVisible });
  },

  setShowTransactionSettingsModal: (isVisible: boolean) => {
    set({ showTransactionSettingsModal: isVisible });
  },

  setShowEmailInviteModal: (isVisible: boolean) => {
    set({ showEmailInviteModal: isVisible });
  },

  setShowFeedbackConfirmDialog: (isVisible: boolean) => {
    set({ showFeedbackConfirmDialog: isVisible });
  },

  setShowGmailConnectorModal: (isVisible: boolean) => {
    set({
      integrations: {
        ...get("integrations"),
        showGmailConnectorModal: isVisible,
      },
    });
  },

  setShowOpenAiApiModal: (isVisible: boolean) => {
    set({
      integrations: {
        ...get("integrations"),
        showOpenAiApiModal: isVisible,
      },
    });
  },

  setShowPrivacySettings: (isVisible: boolean) => {
    set({ showPrivacySettings: isVisible });
  },

  setShowNotificationsSettings: (isVisible: boolean) => {
    set({ showNotificationsSettings: isVisible });
  },

  setShowCreateNewAccountModal: (isVisible: boolean) => {
    set({ showCreateNewAccountModal: isVisible });
  },

  setShowEditAccountModal: (
    isVisible: boolean,
    payload: IAccountModalPayload,
  ) => {
    set({
      showEditAccountModal: isVisible,
      editAccountModalPayload: payload || null,
    });
  },

  setShowEditClientProviderModal: (
    isVisible: boolean,
    payload: IEditClientProviderModalPayload | null,
  ) => {
    set({
      showEditClientProviderModal: isVisible,
      editClientProviderModalPayload: payload || null,
    });
  },

  setShowEditRecurringTransactionModal: (
    isVisible: boolean,
    payload: IRecurringTransactionModalPayload | null,
  ) => {
    set({
      showEditRecurringTransactionModal: isVisible,
      editRecurringTransactionPayload: payload || null,
    });
  },

  setShowCreateRecurringTransactionModal: (isVisible: boolean) => {
    set({
      showCreateRecurringTransactionModal: isVisible,
    });
  },

  setShowRecurringTransactionPaymentModal: (
    isVisible: boolean,
    payload: any,
  ) => {
    set({
      showRecurringTransactionPaymentModal: isVisible,
      recurringTransactionPaymentPayload: payload || null,
    });
  },

  setShowEditCategoryModal: (
    isVisible: boolean,
    payload: IEditCategoryModalPayload | null,
  ) => {
    set({
      showEditCategoryModal: isVisible,
      editCategoryModalPayload: payload || null,
    });
  },

  setShowChangePassword: (isVisible: boolean) => {
    set({ showChangePassword: isVisible });
  },

  setShowContactSearchModal: (isVisible: boolean) => {
    set({ showContactSearchModal: isVisible });
  },

  setShowCreatePassKey: (isVisible: boolean) => {
    set({ showCreatePassKey: isVisible });
  },

  setShowContactQrCode: (isVisible: boolean) => {
    set({ showContactQrCode: isVisible });
  },

  setShowCreateNewTask: (isVisible: boolean) => {
    set({ showCreateNewTask: isVisible });
  },

  setShowCreateNewCanvas: (isVisible: boolean) => {
    set({ showCreateNewCanvas: isVisible });
  },

  setShowCreateNewLink: (isVisible: boolean) => {
    set({ showCreateNewLink: isVisible });
  },

  setShowEditProfile: (isVisible: boolean) => {
    set({ showEditProfile: isVisible });
  },

  setShowEditAccount: (accountId: string) => {
    set({ showEditAccount: accountId });
  },

  setShowDeleteHistoryConfirm: (isVisible: boolean) => {
    set({ showDeleteHistoryConfirm: isVisible });
  },

  setShowLogoutDialog: (isVisible: boolean) => {
    set({ showLogoutDialog: isVisible });
  },

  setShowMoneyTransferModal: (isOpen: boolean) => {
    set({ showMoneyTransferModal: isOpen });
  },

  setShowSearchModal: (isOpen: boolean) => {
    set({ showSearchModal: isOpen });
  },

  setShowTableSettingsModal: (isOpen: boolean) => {
    set({ showTableSettingsModal: isOpen });
  },

  setShowPaginationSettingsModal: (isOpen: boolean) => {
    set({ showPaginationSettingsModal: isOpen });
  },

  setShowDayColorsModal: (isOpen: boolean) => {
    set({ showDayColorsModal: isOpen });
  },

  setShowMonthColorsModal: (isOpen: boolean) => {
    set({ showMonthColorsModal: isOpen });
  },

  setShowChartSettingsModal: (isOpen: boolean) => {
    set({ showChartSettingsModal: isOpen });
  },

  setShowEmailNotificationsSettingsModal: (isOpen: boolean) => {
    set({ showEmailNotificationsSettingsModal: isOpen });
  },

  setShowStartupSettings: (isOpen: boolean) => {
    set({ showStartupSettings: isOpen });
  },
}));

export default useModalsStore;
